import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Link as RouterLink } from 'react-router-dom'
import Avatar from '@material-ui/core/Avatar'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import MessageOutlinedIcon from 'components/material-symbol/icons/Message'
import StreamSendMessageButton from 'components/stream-messaging/send-message-button'
import { followUpSearch } from 'components/student-insights/chat-follow-up-helper'
import { FOLLOW_UP_LIST_PATH } from 'components/student-insights/constants'
import { formatLastLesson } from 'components/student-insights/format'
import { amplitudeEvent } from 'lib/amplitude'
import { getInitials } from 'lib/text-display'
import { SettingsSection } from 'pages/pga-coach/settings/settings-components'
import useSettingsStyles from 'pages/pga-coach/settings/settings.styles'
import { useStudentListNameAgeStyles } from '../student-list-name-age-styles'
import useStyles from './styles'

const NeedsAttentionCard = ({
  insights,
  totalCount,
  loading,
  error,
  canMessage,
}) => {
  const settingsClasses = useSettingsStyles()
  const avatarClasses = useStudentListNameAgeStyles()
  const classes = useStyles()

  let body
  if (loading) {
    body = (
      <Box className={settingsClasses.row}>
        <LinearProgress color="primary" style={{ width: '100%' }} />
      </Box>
    )
  } else if (error) {
    body = (
      <Box className={settingsClasses.row}>
        <Typography className={settingsClasses.rowDescription}>
          We couldn't load your students right now. Please try again later.
        </Typography>
      </Box>
    )
  } else if (insights.length === 0) {
    body = (
      <Box className={settingsClasses.row}>
        <Typography className={settingsClasses.rowDescription}>
          You're all caught up. Students who cancel, are new, or haven't booked
          in a while show up here.
        </Typography>
      </Box>
    )
  } else {
    body = (
      <>
        {insights.map(({ student, reason, lastLessonAt }) => {
          const contactId = student.contact?.externalId

          return (
            <Box
              key={student.id}
              className={clsx(settingsClasses.row, classes.attentionRow)}
            >
              <Avatar className={avatarClasses.studentAvatar}>
                {getInitials(`${student.firstName} ${student.lastName}`)}
              </Avatar>
              <Box className={settingsClasses.rowBody}>
                <RouterLink
                  to={`/pga-coach/student/${student.id}`}
                  className={clsx(
                    settingsClasses.rowTitle,
                    classes.studentName,
                  )}
                >
                  {student.firstName} {student.lastName}
                </RouterLink>
                <Typography
                  component="p"
                  className={settingsClasses.rowDescription}
                >
                  {reason}
                </Typography>
              </Box>
              <Box className={classes.lastLesson}>
                <Typography
                  component="p"
                  className={settingsClasses.sectionEyebrow}
                >
                  Last lesson
                </Typography>
                <Typography
                  component="p"
                  className={settingsClasses.rowDescription}
                >
                  {formatLastLesson(lastLessonAt)}
                </Typography>
              </Box>
              {canMessage && contactId ? (
                <StreamSendMessageButton
                  userIds={[contactId]}
                  search={followUpSearch(student.id)}
                  variant="outlined"
                  size="small"
                  className={classes.followUpButton}
                  classes={{ label: classes.sentenceCaseLabel }}
                  startIcon={<MessageOutlinedIcon />}
                  aria-label={`Follow up with ${student.firstName} ${student.lastName}`}
                  onClick={() =>
                    amplitudeEvent('student-insights-follow-up-opened', {
                      source: 'follow-ups',
                    })
                  }
                >
                  Follow up
                </StreamSendMessageButton>
              ) : null}
            </Box>
          )
        })}
        {totalCount > insights.length ? (
          <Box className={settingsClasses.row}>
            <Button
              component={RouterLink}
              to={FOLLOW_UP_LIST_PATH}
              color="primary"
              size="small"
            >
              View all {totalCount} in your student list
            </Button>
          </Box>
        ) : null}
      </>
    )
  }

  return (
    <SettingsSection
      eyebrow="Needs attention"
      subtitle="Students with a booking signal that may need a personal follow-up."
      classes={settingsClasses}
    >
      {body}
    </SettingsSection>
  )
}

NeedsAttentionCard.propTypes = {
  insights: PropTypes.arrayOf(
    PropTypes.shape({
      reason: PropTypes.string,
      lastLessonAt: PropTypes.string,
      student: PropTypes.shape({
        id: PropTypes.string.isRequired,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        contact: PropTypes.shape({ externalId: PropTypes.string }),
      }).isRequired,
    }),
  ).isRequired,
  totalCount: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  canMessage: PropTypes.bool,
}

NeedsAttentionCard.defaultProps = {
  canMessage: true,
}

export default NeedsAttentionCard
